// Search page island. The server renders page 0 of the results for ?term/?tag/?facet; here we drive
// live search on the page: form submit, facet tabs, tag facets and infinite scroll. Each query POSTs
// to the SearchSurface fragment endpoint and swaps/appends the returned HTML, so the result markup
// stays in Razor (_SiteSearchResults). The header popover has no [data-search] root and submits as
// a plain GET to this page.

const ENDPOINT = '/umbraco/surface/SearchSurface/Results';
const MIN_QUERY_LENGTH = 3;
const ALL_FACET = 'facetall';
const SEARCHING_CLASS = 'isSearching';
const LOADING_MORE_CLASS = 'isLoadingMore';

export function initSearch() {
  document.querySelectorAll('[data-search]').forEach(initSearchRoot);
}

function initSearchRoot(root) {
  const siteId = root.dataset.siteId || '';
  const isGlz = root.dataset.isGlz === 'true';
  const form = root.querySelector('[data-search-form]');
  const input = root.querySelector('.mainSearch');
  const content = root.querySelector('[data-search-content]');
  if (!form || !input || !content) return;

  let facetKey = root.dataset.facetKey || '';
  let facetKeyRaw = root.dataset.facetRaw || '';
  let page = 0;
  let totalPages = 0;
  let loading = false;
  let observer = null;

  const query = () => (input.value || '').trim();
  const resultsContainer = () => content.querySelector('[data-search-results]');
  const hasMore = () => page + 1 < totalPages;

  function pageUrl() {
    let url = location.pathname + '?term=' + encodeURIComponent(query());
    if (!facetKey || facetKey === ALL_FACET) return url;
    url += '&facet=' + encodeURIComponent(facetKey);
    if (facetKeyRaw) url += '&facetRaw=' + encodeURIComponent(facetKeyRaw);
    return url;
  }

  function setLoading(isLoading, className) {
    loading = isLoading;
    root.classList.toggle(className, isLoading);
    content.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  async function fetchFragment(p) {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query(), page: p, siteId, facetKey, facetKeyRaw, isGlz })
    });
    if (!res.ok) throw new Error('search failed: ' + res.status);
    return res.text();
  }

  function watchSentinel() {
    if (observer) observer.disconnect();
    const sentinel = content.querySelector('[data-search-more]');
    if (!sentinel) return;
    sentinel.hidden = !hasMore();
    if (!hasMore()) return;
    observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMore();
    }, { rootMargin: '120px' });
    observer.observe(sentinel);
  }

  function syncFromRendered() {
    const rc = resultsContainer();
    totalPages = rc ? parseInt(rc.dataset.totalPages, 10) || 0 : 0;
    watchSentinel();
  }

  async function runSearch() {
    if (query().length < MIN_QUERY_LENGTH || loading) return;
    setLoading(true, SEARCHING_CLASS);
    try {
      page = 0;
      content.innerHTML = await fetchFragment(0);
      history.replaceState(null, '', pageUrl());
      syncFromRendered();
    } catch (e) { console.error(e); }
    setLoading(false, SEARCHING_CLASS);
  }

  async function loadMore() {
    if (loading || !hasMore()) return;
    setLoading(true, LOADING_MORE_CLASS);
    try {
      const html = await fetchFragment(page + 1);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelectorAll('.searchResults > li');
      const list = content.querySelector('.searchResults');
      if (list) incoming.forEach(li => list.appendChild(li));
      page++;
      watchSentinel();
    } catch (e) { console.error(e); }
    setLoading(false, LOADING_MORE_CLASS);
  }

  form.addEventListener('submit', e => {
    e.preventDefault();
    facetKey = '';
    facetKeyRaw = '';
    runSearch();
  });

  root.addEventListener('click', e => {
    const el = e.target.closest('[data-facet]');
    if (!el || !root.contains(el)) return;
    e.preventDefault();
    facetKey = el.dataset.facet || '';
    facetKeyRaw = el.dataset.facetRaw || '';
    runSearch();
  });

  syncFromRendered();
}
